import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import { publishingService, type MissedPostsChoice } from '@/shared/services/publishing.service';
import { useMyRole } from '@/shared/hooks/useMyRole';

const KEY = 'publishing-pause';

/** Whether publishing is paused for the current brand (Task 1.5). */
export function usePublishingPause() {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [KEY, wsId],
    queryFn: () => publishingService.status(),
    enabled: !!wsId,
    staleTime: 30_000,
  });
}

/** Owners and admins pause and resume (the API refuses anyone else). */
export function useCanPausePublishing(): boolean {
  const { role } = useMyRole();
  return role === 'owner' || role === 'admin';
}

export function usePausePublishing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) => publishingService.pause(reason),
    onSuccess: (data) => {
      qc.setQueriesData({ queryKey: [KEY] }, data);
      void qc.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

export function useResumePublishing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (missed?: MissedPostsChoice) => publishingService.resume(missed),
    onSuccess: (data) => {
      qc.setQueriesData({ queryKey: [KEY] }, data);
      void qc.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}
